import { useState } from 'react'
import { AuthState, auth } from '../store/auth'
import { apiUserUpdateNickname, apiUserChangePassword } from '../server/ServiceCall'
import '../css/App.css'

interface Props {
  authState: AuthState
  /** 닉네임 변경 성공 시 상위에 신규 닉네임 전달 (AuthState 갱신용) */
  onNicknameChanged: (newNickname: string) => void
  onClose: () => void
}

/**
 * SEQ=10110 STEP=4 — 내정보 변경 모달
 * 섹션 1: 닉네임 변경 (LENGTH 1~16, 중복 검증)
 * 섹션 2: 비밀번호 변경 (현재 비번 확인 + 신규 비번 4자 이상 + 확인 일치)
 *   - 변경 성공 시 SESSION_VERSION+1 → 타 기기 토큰 무효화. 현 기기 세션은 유지.
 */
export default function MyInfoModal({ authState, onNicknameChanged, onClose }: Props) {
  // ── 닉네임 섹션 ─────────────────────────────────────
  const [newNick, setNewNick]       = useState<string>('')
  const [nickMsg, setNickMsg]       = useState<string>('')
  const [nickMsgErr, setNickMsgErr] = useState<boolean>(false)
  const [nickBusy, setNickBusy]     = useState<boolean>(false)

  // ── 비밀번호 섹션 ────────────────────────────────────
  const [curPass, setCurPass]   = useState<string>('')
  const [newPass, setNewPass]   = useState<string>('')
  const [confPass, setConfPass] = useState<string>('')
  const [passMsg, setPassMsg]   = useState<string>('')
  const [passMsgErr, setPassMsgErr] = useState<boolean>(false)
  const [passBusy, setPassBusy] = useState<boolean>(false)

  async function handleChangeNickname() {
    setNickMsg('')
    setNickMsgErr(false)
    const trimmed = newNick.trim()
    if (trimmed.length === 0) {
      setNickMsg('새 닉네임을 입력하세요.'); setNickMsgErr(true); return
    }
    if (trimmed.length > 16) {
      setNickMsg('닉네임은 16자 이하여야 합니다.'); setNickMsgErr(true); return
    }
    if (trimmed === authState.nickname) {
      setNickMsg('현재 닉네임과 같습니다.'); setNickMsgErr(true); return
    }

    setNickBusy(true)
    try {
      const r = await apiUserUpdateNickname(authState.token, trimmed)
      if (r.resultCode === '00') {
        // localStorage AuthState 갱신
        const cur = auth.get()
        if (cur) {
          auth.set({ ...cur, nickname: trimmed })
        }
        onNicknameChanged(trimmed)
        setNickMsg('닉네임이 변경되었습니다.')
        setNickMsgErr(false)
        setNewNick('')
      } else {
        setNickMsg(r.resultMessage || '닉네임 변경 실패')
        setNickMsgErr(true)
      }
    } catch {
      setNickMsg('네트워크 오류')
      setNickMsgErr(true)
    } finally {
      setNickBusy(false)
    }
  }

  async function handleChangePassword() {
    setPassMsg('')
    setPassMsgErr(false)
    if (curPass.length === 0) {
      setPassMsg('현재 비밀번호를 입력하세요.'); setPassMsgErr(true); return
    }
    if (newPass.length < 4) {
      setPassMsg('새 비밀번호는 4자 이상이어야 합니다.'); setPassMsgErr(true); return
    }
    if (newPass !== confPass) {
      setPassMsg('새 비밀번호 확인이 일치하지 않습니다.'); setPassMsgErr(true); return
    }
    if (curPass === newPass) {
      setPassMsg('새 비밀번호가 현재 비밀번호와 같습니다.'); setPassMsgErr(true); return
    }

    setPassBusy(true)
    try {
      const r = await apiUserChangePassword(authState.token, curPass, newPass)
      if (r.resultCode === '00') {
        setPassMsg('비밀번호가 변경되었습니다. 다른 기기는 자동 로그아웃됩니다.')
        setPassMsgErr(false)
        setCurPass(''); setNewPass(''); setConfPass('')
      } else {
        setPassMsg(r.resultMessage || '비밀번호 변경 실패')
        setPassMsgErr(true)
      }
    } catch {
      setPassMsg('네트워크 오류')
      setPassMsgErr(true)
    } finally {
      setPassBusy(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box myinfo-modal" onClick={e => e.stopPropagation()}>
        <h3 className="modal-title">내 정보 변경</h3>

        {/* ── 닉네임 섹션 ─────────────────────── */}
        <section className="myinfo-section">
          <h4 className="myinfo-section-title">닉네임</h4>
          <div className="myinfo-row">
            <span className="myinfo-label">현재</span>
            <span className="myinfo-current">{authState.nickname}</span>
          </div>
          <div className="myinfo-row">
            <span className="myinfo-label">변경</span>
            <input
              type="text"
              className="modal-input"
              placeholder="새 닉네임 (1~16자)"
              value={newNick}
              maxLength={16}
              onChange={e => setNewNick(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleChangeNickname()}
              disabled={nickBusy}
            />
            <button
              className="btn-primary"
              onClick={handleChangeNickname}
              disabled={nickBusy}
            >
              {nickBusy ? <span className="spinner" /> : '변경'}
            </button>
          </div>
          {nickMsg && (
            <p className={nickMsgErr ? 'msg' : 'msg-ok'}>{nickMsg}</p>
          )}
        </section>

        <hr className="divider" />

        {/* ── 비밀번호 섹션 ───────────────────── */}
        <section className="myinfo-section">
          <h4 className="myinfo-section-title">비밀번호</h4>
          <div className="myinfo-row">
            <span className="myinfo-label">현재 비번</span>
            <input
              type="password"
              className="modal-input"
              placeholder="현재 비밀번호"
              value={curPass}
              maxLength={64}
              onChange={e => setCurPass(e.target.value)}
              disabled={passBusy}
            />
          </div>
          <div className="myinfo-row">
            <span className="myinfo-label">새 비번</span>
            <input
              type="password"
              className="modal-input"
              placeholder="새 비밀번호 (4자 이상)"
              value={newPass}
              maxLength={64}
              onChange={e => setNewPass(e.target.value)}
              disabled={passBusy}
            />
          </div>
          <div className="myinfo-row">
            <span className="myinfo-label">확인</span>
            <input
              type="password"
              className="modal-input"
              placeholder="새 비밀번호 확인"
              value={confPass}
              maxLength={64}
              onChange={e => setConfPass(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleChangePassword()}
              disabled={passBusy}
            />
            <button
              className="btn-primary"
              onClick={handleChangePassword}
              disabled={passBusy}
            >
              {passBusy ? <span className="spinner" /> : '변경'}
            </button>
          </div>
          {passMsg && (
            <p className={passMsgErr ? 'msg' : 'msg-ok'}>{passMsg}</p>
          )}
          <p className="muted" style={{ fontSize: '0.85em', marginTop: 8 }}>
            비밀번호 변경 시 다른 기기 세션은 자동 종료됩니다. 현재 기기는 유지됩니다.
          </p>
        </section>

        <div className="modal-btns" style={{ marginTop: 16 }}>
          <button className="btn-cancel" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  )
}
